import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@carrent/api'

/**
 * Данные всех гаджетов панели.
 *
 * Один запрос на всю панель, а не по запросу на гаджет: десять гаджетов
 * с автообновлением раз в пятнадцать секунд иначе дают сорок запросов
 * в минуту на ровном месте.
 *
 * Выключенные гаджеты в запрос не попадают — отключить гаджет значит
 * в том числе перестать за него платить.
 */
export function useWidgetData(layout, intervalSec) {
  const [results, setResults] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [updatedAt, setUpdatedAt] = useState(null)

  // Раскладка меняется при каждой перестановке, а перезапрашивать данные
  // из-за смены позиции незачем. Ключ описывает только то, что влияет
  // на ответ сервера: состав включённых гаджетов и их настройки.
  const requestKey = JSON.stringify(
    (layout || [])
      .filter((w) => w.enabled)
      .map((w) => [w.id, w.type, w.settings])
      .sort((a, b) => (a[0] < b[0] ? -1 : 1)),
  )

  const keyRef = useRef(requestKey)
  keyRef.current = requestKey

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      const widgets = JSON.parse(keyRef.current).map(([id, type, settings]) => ({
        id,
        type,
        settings,
      }))

      if (widgets.length === 0) {
        setResults({})
        setLoading(false)
        setError(null)
        return
      }

      if (!silent) setLoading(true)
      try {
        const { data } = await api.post('/admin/dashboards/widget-data', { widgets })
        setResults(data.data.results || {})
        setError(null)
        setUpdatedAt(new Date())
      } catch {
        setError('Не удалось загрузить данные')
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    fetchData()
  }, [requestKey, fetchData])

  // Автообновление. Пока вкладка скрыта, опрос не идёт: панель на втором
  // мониторе не должна ходить в базу всю ночь. При возврате — сразу свежие
  // данные, иначе человек несколько секунд смотрит на устаревшие цифры.
  useEffect(() => {
    if (!intervalSec) return undefined

    let timer = null
    const start = () => {
      stop()
      timer = setInterval(() => fetchData({ silent: true }), intervalSec * 1000)
    }
    const stop = () => {
      if (timer) clearInterval(timer)
      timer = null
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchData({ silent: true })
        start()
      } else {
        stop()
      }
    }

    if (document.visibilityState === 'visible') start()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [intervalSec, fetchData])

  return { results, loading, error, updatedAt, refresh: () => fetchData({ silent: true }) }
}
